/** Shared horizontal-swipe classification used by the puzzle grid and other
 *  views (e.g. the leaderboard) for prev/next navigation. */

const SWIPE_THRESHOLD = 60; // px of horizontal travel to count as a navigation swipe
const SWIPE_RATIO = 1.5; // horizontal travel must dominate vertical by this factor
const FLICK_VELOCITY = 0.5; // px/ms — a horizontal flick this fast overrides a tile drag

/**
 * Classify a gesture's net displacement as a horizontal navigation swipe.
 * Swipe left (dx < 0) advances to the next item; swipe right goes back.
 * Returns null for taps, short drags, or vertical/diagonal gestures (which
 * should scroll rather than navigate).
 */
export function detectSwipe(dx: number, dy: number): "prev" | "next" | null {
  if (Math.abs(dx) < SWIPE_THRESHOLD) return null;
  if (Math.abs(dx) < Math.abs(dy) * SWIPE_RATIO) return null;
  return dx < 0 ? "next" : "prev";
}

/** A fast horizontal flick — used to let a swipe win even when it began on a
 *  tile (which would otherwise be a deliberate drag/swap). dt<=0 (synchronous
 *  test events) counts as a flick. */
export function isFlick(dx: number, dtMs: number): boolean {
  return dtMs <= 0 ? true : Math.abs(dx) / dtMs >= FLICK_VELOCITY;
}

/**
 * Decide what a completed gesture over the grid was. A qualifying horizontal
 * swipe navigates when no tile was grabbed; when a tile drag was active it only
 * navigates if it was a fast flick, so deliberate (aimed, slower) drags swap.
 */
export function decideSwipe(
  dx: number, dy: number, dtMs: number, tileDragActive: boolean,
): "prev" | "next" | null {
  const dir = detectSwipe(dx, dy);
  if (!dir) return null;
  if (!tileDragActive) return dir;
  return isFlick(dx, dtMs) ? dir : null;
}
